/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Nov 06 2024
 **/

import produce from "immer";
import { has, isEmpty, omit } from "lodash-es";

import { Configure } from "@/frameworks/libraries/utilities";
import { usePersistActions, usePersistStore } from "@/frameworks/core/persistence";
import { MyRoomService, SpaceService, UserService } from "@/frameworks/core/API";

import { SpaceDispatch } from "./states";
import {
	formatExternalGroupSpace,
	formatfetchedGroupSpace,
	formatfetchedMyRoom,
	formatfetchedSpaceDetail,
	validate_space_name
} from "./utils";
import { queryClientStatic } from "../../API/private";
import MyRoomServiceAPI from "../../API/user/useMyRoom/MyRoomServiceAPI";

const LOGTAG = "#Space:Actions#";
export default class SpaceActions implements SpaceDispatch {
	private static _instance: SpaceActions | null = null;
	public static get instance() {
		if (!SpaceActions._instance) {
			SpaceActions._instance = new SpaceActions();
		}
		return SpaceActions._instance;
	}
	private constructor() {}
	private queryClient = queryClientStatic;
	private spaceAPI = new SpaceService({});
	private userAPI = new UserService({});
	private MyRoomAPI = MyRoomServiceAPI.getInstance({});

	public async on_message_received(
		{
			type,
			payload
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		}: Message.Frame<Message.StompMessageTypes, any, Message.Options>,
		topic: string
	): Promise<void> {
		switch (type) {
			case "spaceParticipantCountUpdated": {
				this.space_participant_count_updated(payload);
				break;
			}
			case "spaceInfoUpdated": {
				this.space_info_updated(payload);
				break;
			}
			case "spaceParticipated": {
				this.space_participated(payload);
				break;
			}
			case "spaceNewNotification": {
				this.space_new_notification(payload);
				break;
			}
			case "spaceJoinedUserCountUpdated": {
				this.space_joined_user_count_updated(payload);
				break;
			}
			case "groupInvited":
			case "groupWithdrawn":
			case "spaceInvited":
			case "spaceDeleted":
			case "groupAcceptJoinRequest":
			case "groupRejectJoinRequest":
			case "spaceAcceptJoinRequest":
			case "spaceRejectJoinRequest": {
				this.initialize();
				break;
			}
			default: {
				break;
			}
		}
	}

	public async initialize(): Promise<void> {
		const data = await this.refresh_space_all();
		console.debug(LOGTAG, "initialize", data);

		if (data) {
			data["myRoom"] &&
				(() => {
					const space = formatfetchedMyRoom(data.myRoom);
					if (space) {
						this.set_space(data.myRoom.id || "", space);
					}
				})();
			data["openWorlds"] &&
				(() => {
					data["openWorlds"].forEach((openWorld) => {
						const space = {
							feSpaceType: "OPEN_WORLD",
							participantCount: openWorld.participantCount,
							participantUserIDs: [],
							spaceId: openWorld.spaceId,
							spaceName: openWorld.name,
							thumbnailPath: openWorld.thumbnailPath,
							timestamp: Date.now(),
							updatedMethod: "restAPI"
						} as V2.OpenWorldSpaceInfo;

						if (space) {
							this.set_space(openWorld.spaceId || "", space);
						}
					});
				})();
			data["groups"] &&
				data["groups"].forEach((group) => {
					group.spaces.forEach((space) => {
						const groupSpace = formatfetchedGroupSpace(space, group.id);
						if (groupSpace) {
							this.set_space(space.spaceId, groupSpace);
						}
					});
					group.groupOpenWorlds.forEach((openWorld) => {
						const groupOpenWorldSpace = {
							feSpaceType: "OPEN_WORLD",
							participantCount: openWorld.participantCount,
							participantUserIDs: [],
							spaceId: openWorld.spaceId,
							groupId: group.id,
							spaceName: openWorld.spaceName,
							thumbnailPath: openWorld.thumbnailPath,
							timestamp: Date.now(),
							updatedMethod: "restAPI"
						} as V2.OpenWorldSpaceInfo;

						if (groupOpenWorldSpace) {
							this.set_space(groupOpenWorldSpace.spaceId || "", groupOpenWorldSpace);
						}
					});
				});
			data["externalGroup"] &&
				data["externalGroup"].forEach((space) => {
					const groupSpace = formatExternalGroupSpace(space);
					if (groupSpace) {
						this.set_space(space.spaceId, groupSpace);
					}
				});
		}
	}

	public async refresh_space(spaceID: SPACE_ID): Promise<V2.SpaceInfo | null | undefined> {
		console.debug(LOGTAG, "refresh_space", spaceID);
		if (!Configure.is_authenticated) {
			return null;
		}
		const data = await this.fetch_space(spaceID);

		if (data && !isEmpty(data)) {
			this.update_space(spaceID, data as V2.SpaceInfo);
		}
		return data;
	}

	public async refresh_space_all(): Promise<WebServices.Space.MySpaces.List.Data> {
		this.fetch_space_invite();
		return this.fetch_space_all();
	}

	private async fetch_space_all(): Promise<WebServices.Space.MySpaces.List.Data> {
		await this.queryClient.invalidateQueries(["space", "mySpaces"]);
		return queryClientStatic.fetchQuery({
			queryKey: ["space", "mySpaces"],
			queryFn: async () => {
				const { data } = await this.spaceAPI.space.mySpaces.list.execute();
				return data;
			}
		});
	}

	private async fetch_space(spaceID: SPACE_ID) {
		return this.queryClient.fetchQuery({
			queryKey: ["space", spaceID],
			queryFn: async () => {
				try {
					/**
					 * 스페이스 관련 데이터 조회시, API가 분리가 되어있으나 fe쪽에서 [myRoom || space...] 타입 다루기 힘듬으로 해당 플로우로 처리
					 */
					const data = await this.spaceAPI.space.detail
						.execute({ spaceId: spaceID })
						.then((res) => {
							return formatfetchedSpaceDetail(res.data);
						})
						.catch(() => {
							return (async () => {
								return await this.MyRoomAPI.myRoom.inquiry
									.execute({
										myRoomId: spaceID
									})
									.then((res) => {
										return formatfetchedMyRoom(res.data);
									});
							})();
						});
					return data;
				} catch (error) {
					console.error("fetch_space error...", error);
				}
			}
		});
	}

	public get_space(spaceID: SPACE_ID): V2.SpaceInfo | null {
		const queryData = this.queryClient.getQueryData<V2.SpaceInfo>(["space", spaceID, "OPEN_WORLD"]);
		if (queryData) {
			return queryData;
		}
		return null;
	}

	public set_space(spaceID: SPACE_ID, info: V2.SpaceInfo): void {
		//@ts-ignore
		this.queryClient.setQueryData(["space", spaceID, info.feSpaceType], (old: V2.SpaceInfo) => {
			return {
				//@ts-ignore
				...old,
				...info,
				...validate_space_name(info?.name || info.spaceName, info),
				timestamp: Date.now(),
				updatedMethod: "restAPI"
			};
		});
	}

	public update_space(spaceID: SPACE_ID, info: Partial<V2.SpaceInfo>): void {
		const prev = this.get_space(spaceID);
		if (!prev) return;

		this.set_space(spaceID, {
			...prev,
			...info
		} as V2.SpaceInfo);
	}

	/**
	 *	스페이스 pin 상태 변경 (groupSpace만 가능)
	 * @param spaceID string
	 */
	public async update_pin(spaceID: SPACE_ID) {
		try {
			//@ts-ignore pinned data not exist
			const pinned = (this.get_space(spaceID) as V2.GroupSpaceInfo)?.pinned;
			await this.spaceAPI.spaceUser.pin.execute({ spaceId: spaceID, pinned: !pinned });
			this.update_space(spaceID, { pinned: !pinned });
		} catch (error) {
			console.error("update_pin error...", error);
		}
	}

	/**
	 * 스페이스 채팅 알림 설정 변경
	 * @param spaceID
	 * @returns
	 */
	public async update_space_chat_notification(spaceID: SPACE_ID): Promise<void> {
		try {
			const prev = this.get_space(spaceID);
			if (!prev) return;
			if (prev.feSpaceType === "MY_ROOM") {
				await this.MyRoomAPI.myRoomNotification.updateSpaceChatNotification.execute({
					myRoomId: spaceID,
					useSpaceChatNotification: !prev.useSpaceChatNotification
				});
			} else {
				await this.spaceAPI.spaceNotification.UpdateSpaceChatNotification.execute({
					spaceId: spaceID,
					useSpaceChatNotification: !prev.useSpaceChatNotification
				});
			}

			this.update_space(spaceID, {
				useSpaceChatNotification: !prev.useSpaceChatNotification
			});
		} catch (error) {
			console.error("update_space_chat_notification error...", error);
		}
	}

	/**
	 * do not use this function directly
	 * @param spaceID
	 * @returns
	 */
	public refresh_space_user_list(spaceID: SPACE_ID) {
		const prev = this.get_space(spaceID);
		const psersistAcrtions = usePersistActions();
		if (prev) {
			const isRequired = prev.participantUserIDs.some((id) => {
				return !has(psersistAcrtions.users.get_user(id), "authority");
			});

			// 스페이스에 참여한 사용자들의 권한 필드가 있으면 굳이 안돌려도됨.
			if (isRequired) {
				return this.fetch_space_user_list(spaceID).then((res) => {
					res.forEach((user) => {
						psersistAcrtions.users.set_user(user.id, user);
					});
				});
			}
		}
	}

	/**
	 * 스페이스 강제 퇴장
	 */
	public async force_kick_out(spaceID: SPACE_ID, spaceType: FE_SPACE_TYPE, cause: string) {
		try {
			usePersistStore.setState((state) => ({
				...state,
				space: {
					...state.space,
					is_force_kick_out: {
						cause: "force_kick_out",
						spaceID,
						spaceType
					}
				}
			}));
			const KICK_OUT_TIME = 1500;
			setTimeout(() => {
				window.location.reload();
			}, KICK_OUT_TIME);
		} catch (error) {
			console.error("force_kick_out error...", error);
		}
	}
	public reset_force_kick_out() {
		const state = usePersistStore.getState();
		if (state?.space?.is_force_kick_out) {
			const t = omit(state, "space.is_force_kick_out");
			usePersistStore.setState(t);
		}
	}
	private space_participant_count_updated(data: PushServices.Space.SpaceParticipantCountUpdated.Data) {
		const prev = this.get_space(data.spaceInfo.id);

		if (prev) {
			this.set_space(data.spaceInfo.id, {
				...prev,
				participantUserIDs: data.participants.filter((user) => user.participated).map((user) => user.id),
				participantCount: data.participants.filter((user) => user.participated).length
			});
		}
	}
	/**
	 * update space user list to get space Authority
	 */
	private async fetch_space_user_list(spaceID: SPACE_ID): Promise<WebServices.SpaceUser.List.Data> {
		return this.queryClient.fetchQuery({
			queryKey: ["space:user:list", spaceID],
			queryFn: async () => await this.spaceAPI.spaceUser.list.execute({ spaceId: spaceID })
		});
	}
	/**
	 * refetch_to_recognize_when_space updated at the same time
	 */
	private async fetch_space_invite(): Promise<WebServices.User.InvitesList.Data> {
		return this.queryClient.fetchQuery({
			queryKey: ["user:invite:list"],
			queryFn: async () => await this.userAPI.user.invitesList.execute()
		});
	}

	private space_info_updated(data: PushServices.Space.SpaceInfoUpdated.Data) {
		const prev = this.get_space(data.spaceInfo.id);

		if (prev) {
			this.update_space(data.spaceInfo.id, { ...data.spaceInfo, ...data.groupUpdatedInfo });
		} else {
			console.warn(LOGTAG, "Failed to update space info", data);
			this.refresh_space(data.spaceInfo.id);
		}
	}

	/**
	 * @description
	 * totalUserCount updated joined in space
	 */
	private space_joined_user_count_updated(data: PushServices.Space.SpaceJoinedUserCountUpdated.Data) {
		const prev = this.get_space(data.spaceInfo.id);

		if (prev?.feSpaceType === "GROUP_SPACE") {
			if (prev) {
				this.update_space(data.spaceInfo.id, {
					totalUserCount: data.count
				});
			}
		}
	}
	private space_new_notification(data: PushServices.Space.SpaceNewNotification.Data) {
		const prev = this.get_space(data.spaceId);
		if (prev) {
			this.set_space(data.spaceId, {
				...prev,
				newNotification: true
			});
		}
	}
	private async space_participated(data: PushServices.Space.SpaceParticipated.Data) {
		const res = await this.fetch_space(data.spaceInfo.id);
		if (res) {
			this.set_space(data.spaceInfo.id, res);
		}
	}
}
